/**
 * ConnectionDetailsPopover - Shows connection details in a popover
 *
 * Displays connection metadata including:
 * - Display name and connection ID
 * - Creation date
 * - Authentication type
 * - Connection status with test option
 *
 * Part of P1.1: Connection Details Popover
 */

import { useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Spinner,
  Divider,
} from '@fluentui/react-components';
import {
  MoreHorizontalRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  ArrowSyncRegular,
  InfoRegular,
  CalendarRegular,
  KeyRegular,
  LinkRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import type { ConnectionReference, ConnectionStatus } from '@/types/connectionReference';
import { ConnectionRuntimeSource } from '@/types/connectionReference';
import { formatDate as canonicalFormatDate } from '@/utils/dateTime';
import {
  getConnectionStatusLabel,
  getConnectionStatusIconKind,
} from '@/utils/connections';

const useStyles = makeStyles({
  triggerButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },

  popoverSurface: {
    padding: tokens.spacingVerticalM,
    minWidth: '280px',
    maxWidth: '320px',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },

  headerIcon: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  headerIconSymbol: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground3,
  },

  headerText: {
    flex: 1,
    minWidth: 0,
  },

  connectionName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  connectionType: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },

  divider: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },

  detailsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },

  detailIcon: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
    flexShrink: 0,
  },

  detailContent: {
    flex: 1,
    minWidth: 0,
  },

  detailLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },

  detailValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  detailValueMono: {
    fontSize: tokens.fontSizeBase100,
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorNeutralForeground2,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  statusSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },

  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  statusIconConnected: {
    fontSize: '16px',
    color: tokens.colorPaletteGreenForeground1,
  },

  statusIconError: {
    fontSize: '16px',
    color: tokens.colorPaletteRedForeground1,
  },

  statusIconWarning: {
    fontSize: '16px',
    color: tokens.colorPaletteYellowForeground1,
  },

  statusText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },

  statusTextConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },

  statusTextError: {
    color: tokens.colorPaletteRedForeground1,
  },

  statusTextWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },

  testButton: {
    minWidth: 'auto',
  },

  perUserBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    marginLeft: tokens.spacingHorizontalXS,
  },

  perUserIcon: {
    fontSize: '10px',
  },

  errorMessage: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalXXS,
  },
});

export interface ConnectionDetailsPopoverProps {
  /** Connection reference key */
  referenceKey: string;
  /** Connection reference data */
  reference: ConnectionReference;
  /** Display name of the connection */
  displayName: string;
  /** Connection status */
  status: ConnectionStatus;
  /** Error message if status is error */
  errorMessage?: string | undefined;
  /** Connector type for display */
  connectorType?: string | undefined;
  /** Callback to test the connection */
  onTestConnection?: ((referenceKey: string) => Promise<boolean>) | undefined;
}

/**
 * ConnectionDetailsPopover - Popover showing connection details
 */
export const ConnectionDetailsPopover = ({
  referenceKey,
  reference,
  displayName,
  status,
  errorMessage,
  connectorType,
  onTestConnection,
}: ConnectionDetailsPopoverProps) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<boolean | null>(null);

  // Determine authentication type from reference
  const authType = reference.authentication?.type ?? 'Unknown';
  const authTypeDisplay = getAuthTypeDisplay(authType);

  // Check if this is a dynamic/OBO connection
  const isDynamic = reference.runtimeSource === ConnectionRuntimeSource.Dynamic;

  // Extract connection ID (last part of the path)
  const connectionId = reference.connection.id.split('/').pop() ?? referenceKey;

  // Handle test connection
  const handleTestConnection = useCallback(async () => {
    if (!onTestConnection) return;

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await onTestConnection(referenceKey);
      setTestResult(result);
    } catch {
      setTestResult(false);
    } finally {
      setIsTesting(false);
    }
  }, [onTestConnection, referenceKey]);

  // Get status display
  const getStatusDisplay = () => {
    if (isTesting) {
      return (
        <div className={styles.statusIndicator}>
          <Spinner size="tiny" />
          <Text className={styles.statusText}>Testing...</Text>
        </div>
      );
    }

    if (testResult !== null) {
      return testResult ? (
        <div className={styles.statusIndicator}>
          <CheckmarkCircleFilled className={styles.statusIconConnected} />
          <Text className={`${styles.statusText} ${styles.statusTextConnected}`}>
            Test passed
          </Text>
        </div>
      ) : (
        <div className={styles.statusIndicator}>
          <DismissCircleFilled className={styles.statusIconError} />
          <Text className={`${styles.statusText} ${styles.statusTextError}`}>
            Test failed
          </Text>
        </div>
      );
    }

    const iconKind = getConnectionStatusIconKind(status);

    if (iconKind === 'neutral') {
      return (
        <div className={styles.statusIndicator}>
          <InfoRegular className={styles.detailIcon} />
          <Text className={styles.statusText}>Unknown</Text>
        </div>
      );
    }

    const icon =
      iconKind === 'success' ? (
        <CheckmarkCircleFilled className={styles.statusIconConnected} />
      ) : iconKind === 'error' ? (
        <DismissCircleFilled className={styles.statusIconError} />
      ) : (
        <DismissCircleFilled className={styles.statusIconWarning} />
      );
    const textClass =
      iconKind === 'success'
        ? styles.statusTextConnected
        : iconKind === 'error'
          ? styles.statusTextError
          : styles.statusTextWarning;

    return (
      <div className={styles.statusIndicator}>
        {icon}
        <Text className={`${styles.statusText} ${textClass}`}>
          {getConnectionStatusLabel(status)}
        </Text>
      </div>
    );
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(_e, data) => setIsOpen(data.open)}
      positioning="below-end"
    >
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          icon={<MoreHorizontalRegular />}
          className={styles.triggerButton}
          aria-label="Connection details"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        />
      </PopoverTrigger>

      <PopoverSurface className={styles.popoverSurface}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <LinkRegular className={styles.headerIconSymbol} />
          </div>
          <div className={styles.headerText}>
            <Text className={styles.connectionName}>{displayName}</Text>
            <Text className={styles.connectionType}>
              {connectorType ?? 'Connection'}
              {isDynamic && (
                <span className={styles.perUserBadge}>
                  <PersonRegular className={styles.perUserIcon} />
                  Per-user
                </span>
              )}
            </Text>
          </div>
        </div>

        <Divider className={styles.divider} />

        {/* Details Grid */}
        <div className={styles.detailsGrid}>
          {/* Connection ID */}
          <div className={styles.detailRow}>
            <InfoRegular className={styles.detailIcon} />
            <div className={styles.detailContent}>
              <Text className={styles.detailLabel}>Connection ID</Text>
              <Text className={styles.detailValueMono}>{connectionId}</Text>
            </div>
          </div>

          {/* Reference Key */}
          <div className={styles.detailRow}>
            <KeyRegular className={styles.detailIcon} />
            <div className={styles.detailContent}>
              <Text className={styles.detailLabel}>Reference Key</Text>
              <Text className={styles.detailValueMono}>{referenceKey}</Text>
            </div>
          </div>

          {/* Authentication Type */}
          <div className={styles.detailRow}>
            <KeyRegular className={styles.detailIcon} />
            <div className={styles.detailContent}>
              <Text className={styles.detailLabel}>Authentication</Text>
              <Text className={styles.detailValue}>{authTypeDisplay}</Text>
            </div>
          </div>

          {/* Runtime URL (if available) */}
          {reference.connectionRuntimeUrl && (
            <div className={styles.detailRow}>
              <LinkRegular className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <Text className={styles.detailLabel}>Runtime URL</Text>
                <Text className={styles.detailValueMono}>
                  {reference.connectionRuntimeUrl}
                </Text>
              </div>
            </div>
          )}

          {/* Created date (if available in connectionProperties) */}
          {typeof reference.connectionProperties?.createdTime === 'string' && (
            <div className={styles.detailRow}>
              <CalendarRegular className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <Text className={styles.detailLabel}>Created</Text>
                <Text className={styles.detailValue}>
                  {formatDate(reference.connectionProperties.createdTime)}
                </Text>
              </div>
            </div>
          )}
        </div>

        {/* Status Section */}
        <div className={styles.statusSection}>
          {getStatusDisplay()}
          {onTestConnection && (
            <Button
              appearance="subtle"
              icon={<ArrowSyncRegular />}
              className={styles.testButton}
              onClick={handleTestConnection}
              disabled={isTesting}
            >
              Test
            </Button>
          )}
        </div>

        {/* Error Message */}
        {errorMessage && status === 'error' && (
          <Text className={styles.errorMessage}>{errorMessage}</Text>
        )}
      </PopoverSurface>
    </Popover>
  );
};

ConnectionDetailsPopover.displayName = 'ConnectionDetailsPopover';

/**
 * Get human-readable authentication type display
 */
function getAuthTypeDisplay(authType: string): string {
  switch (authType.toLowerCase()) {
    case 'managedserviceidentity':
      return 'Managed Identity';
    case 'raw':
      return 'API Key';
    case 'key':
      return 'API Key';
    case 'oauth':
    case 'oauth2':
      return 'OAuth 2.0';
    case 'activedirectoryoauth':
      return 'Azure AD OAuth';
    case 'basic':
      return 'Basic Auth';
    case 'certificate':
      return 'Certificate';
    default:
      return authType || 'Unknown';
  }
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  return canonicalFormatDate(dateString, { fallback: dateString });
}
